import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext.tsx';
import { MediaShowcaseItem } from '../types.ts';
import { DEFAULT_MEDIA_SHOWCASE } from '../components/home/MediaShowcaseSection.tsx';
import {
  Image as ImageIcon,
  ArrowLeft,
  X,
  Star,
  ChevronLeft,
  ChevronRight,
  Maximize2,
} from 'lucide-react';

export default function MediaShowcaseCatalog() {
  const { settings, navigate } = useApp();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const primaryColor = settings?.theme?.primaryColor || '#059669';

  const mediaList: MediaShowcaseItem[] =
    settings?.mediaShowcase && settings.mediaShowcase.length > 0
      ? [...settings.mediaShowcase].sort((a, b) => (a.order || 0) - (b.order || 0))
      : DEFAULT_MEDIA_SHOWCASE;

  const showcaseSec = settings?.sections?.find((s) => s.type === 'media_showcase');
  const pageTitle = showcaseSec?.title || 'Galeri Foto & Sorotan Visual';
  const pageSubtitle = showcaseSec?.subtitle || 'Dokumentasi lengkap momen berkesan, pameran inovasi, laboratorium kampus, uji coba teknologi bersih, serta forum diskusi kemitraan bersama industri.';

  useEffect(() => {
    const customTab = settings?.pageTabTitles?.mediaShowcase?.trim();
    if (customTab) {
      document.title = customTab;
    } else {
      const suffix = settings?.pageTabTitles?.titleSuffix !== undefined ? settings.pageTabTitles.titleSuffix : ` | ${settings?.siteName || ''}`;
      document.title = `${pageTitle}${suffix}`;
    }
  }, [pageTitle, settings?.siteName, settings?.pageTabTitles?.mediaShowcase, settings?.pageTabTitles?.titleSuffix]);

  return (
    <div id="media-showcase-catalog-page" className="py-8 sm:py-12 bg-slate-50/50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Header Navigation */}
        <div className="flex items-center justify-between border-b border-slate-200/80 pb-4">
          <nav className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <button
              type="button"
              onClick={() => navigate('home')}
              className="hover:text-emerald-700 transition-colors cursor-pointer"
            >
              Beranda
            </button>
            <span>/</span>
            <span className="text-slate-900 font-bold">{pageTitle}</span>
          </nav>

          <button
            type="button"
            onClick={() => navigate('home')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-2xs transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Kembali ke Beranda</span>
          </button>
        </div>

        {/* Page Title & Context */}
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight flex items-center gap-2.5">
            <ImageIcon className="w-8 h-8 text-emerald-600 shrink-0" />
            <span>{pageTitle}</span>
          </h1>
          {pageSubtitle && (
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              {pageSubtitle}
            </p>
          )}
        </div>

        {/* Dynamic Image Grid */}
        {mediaList.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-3xl border border-slate-200 shadow-2xs max-w-lg mx-auto space-y-3">
            <ImageIcon className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="font-bold text-slate-800">Galeri Foto Kosong</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Belum ada foto yang diunggah ke showcase media. Masuk ke Dashboard Admin untuk menambahkan koleksi foto baru.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
            {mediaList.map((item, idx) => (
              <article
                key={item.id || idx}
                onClick={() => setLightboxIndex(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxIndex(idx);
                  }
                }}
                tabIndex={0}
                role="button"
                className="group bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs hover:shadow-lg hover:border-emerald-300 transition-all duration-300 cursor-pointer flex flex-col focus-visible:ring-2 focus-visible:ring-emerald-500 outline-hidden"
              >
                {/* Photo Top with Zoom Overlay */}
                <div className="relative aspect-4/3 overflow-hidden bg-slate-100 shrink-0">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="p-2.5 rounded-full bg-white/25 backdrop-blur-xs text-white border border-white/20 transform scale-90 group-hover:scale-100 transition-transform duration-300 shadow-lg">
                      <Maximize2 className="w-5 h-5" />
                    </div>
                  </div>

                  {item.badge && (
                    <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-md bg-black/75 backdrop-blur-xs text-white font-extrabold text-[9px] uppercase tracking-wider border border-white/10 shadow-sm">
                      {item.badge}
                    </span>
                  )}
                </div>

                {/* Body Details */}
                <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                  <div className="space-y-1.5">
                    <h3 className="font-extrabold text-slate-900 text-sm sm:text-base group-hover:text-emerald-700 transition-colors line-clamp-1">
                      {item.title}
                    </h3>
                    {item.caption && (
                      <p className="text-slate-500 text-xs leading-relaxed line-clamp-2">
                        {item.caption}
                      </p>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

      </div>

      {/* Lightbox Modal for Fullscreen Photo Viewing */}
      {lightboxIndex !== null && mediaList[lightboxIndex] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/95 backdrop-blur-md animate-in fade-in duration-200">
          <button
            type="button"
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer z-50 shadow-lg"
            title="Tutup (Esc)"
          >
            <X className="w-6 h-6" />
          </button>

          {mediaList.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setLightboxIndex((prev) => (prev !== null ? (prev - 1 + mediaList.length) % mediaList.length : 0))}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-all cursor-pointer z-50 shadow-lg"
                title="Foto Sebelumnya"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={() => setLightboxIndex((prev) => (prev !== null ? (prev + 1) % mediaList.length : 0))}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-all cursor-pointer z-50 shadow-lg"
                title="Foto Selanjutnya"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div className="max-w-5xl w-full flex flex-col items-center">
            <img
              src={mediaList[lightboxIndex].imageUrl}
              alt={mediaList[lightboxIndex].title || 'Showcase'}
              className="max-h-[78vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl border border-white/15"
            />
            {(mediaList[lightboxIndex].title || mediaList[lightboxIndex].caption) && (
              <div className="text-center mt-4 text-white max-w-2xl px-4">
                {mediaList[lightboxIndex].title && (
                  <h3 className="text-lg sm:text-xl font-bold tracking-tight">{mediaList[lightboxIndex].title}</h3>
                )}
                {mediaList[lightboxIndex].caption && (
                  <p className="text-xs sm:text-sm text-slate-300 mt-1.5 leading-relaxed font-medium">
                    {mediaList[lightboxIndex].caption}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
